import { useEffect } from 'react';
import { ActionIcon, Badge, Box, Button, Card, Group, Menu, Modal, Stack, Text } from '@mantine/core';
import { useLiveQuery } from 'dexie-react-hooks';
import { IconCoin, IconDots, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { Link } from 'wouter';
import { dateRange, DEFAULT_FORMAT, eventsJudged, formatById, nextRoundFormatId, suggestedResolution, sideOfSlot, teamOnSide, type Round, type RoundStatus, type Tournament } from '@flow/core';
import { useDeps, useUseCases } from '../../app/services';
import { paths, useQueryState } from '../../app/routes';
import { PageShell, Section } from '../../ui/PageShell';
import { RoundForm, emptyRoundForm } from '../today/RoundForm';
import { CoinFlipModal } from '../today/CoinFlipModal';
import { K } from '../../ui/Kbd';
import { RoundSummary } from '../ai/RoundSummary';
import { TournamentForm } from './TournamentForm';
import { useDeleteTournament } from './useDeleteTournament';

const statusLabel: Record<RoundStatus, { label: string; color: string }> = {
  scheduled: { label: 'scheduled', color: 'gray' },
  inRound: { label: 'in round', color: 'blue' },
  deciding: { label: 'deciding', color: 'yellow' },
  committed: { label: 'decided', color: 'orange' },
  submitted: { label: '✓ submitted', color: 'green' },
};

export const sortRounds = (rs: Round[]) =>
  [...rs].sort((a, b) => (a.startTime || '99').localeCompare(b.startTime || '99') || a.createdAt - b.createdAt);

export const sidesSummary = (r: Round) => {
  if (r.teamASide === null) return 'sides not set';
  const aSide = r.format.sideLabels[r.teamASide];
  const bSide = r.format.sideLabels[(1 - r.teamASide) as 0 | 1];
  const first = sideOfSlot(r, 'first');
  const firstTeam = first === null ? null : teamOnSide(r, first);
  return `${r.teamA.code} ${aSide} · ${r.teamB.code} ${bSide}${firstTeam ? ` · ${firstTeam === 'A' ? r.teamA.code : r.teamB.code} 1st` : ''}`;
};

/** The round's page; a decided round opens on its ballot. */
export const roundHref = (r: Round) => paths.round(r.tournamentId, r.id, r.status === 'committed' || r.status === 'submitted' ? 'ballot' : 'flow');

export const TournamentPage = ({ tid }: { tid: string }) => {
  const deps = useDeps();
  const tournament = useLiveQuery(async () => (await deps.tournaments.get(tid)) ?? null, [tid]);
  if (tournament === undefined) return null;
  if (tournament === null) {
    return (
      <PageShell title="Not found" crumbs={[{ label: 'Tournaments', href: paths.dashboard }]}>
        <Text mb="md">This tournament isn't on this device. It may have been deleted.</Text>
        <Button component={Link} href={paths.dashboard}>Your tournaments</Button>
      </PageShell>
    );
  }
  return <TournamentView tournament={tournament} />;
};

/** Dialogs are URL state: ?modal=new-round | edit-round&round=… | sides&round=… | edit-tournament */
const TournamentView = ({ tournament }: { tournament: Tournament }) => {
  const deps = useDeps();
  const uc = useUseCases();
  const url = useQueryState();
  const rounds = useLiveQuery(async () => sortRounds(await deps.rounds.listByTournament(tournament.id)), [tournament.id]);
  // The judge's latest round anywhere: the event a tournament's first round starts with.
  const latestAnywhere = useLiveQuery(async () => {
    const all = (await Promise.all((await deps.tournaments.list()).map((t) => deps.rounds.listByTournament(t.id)))).flat();
    return all.sort((a, b) => b.createdAt - a.createdAt)[0] ?? null;
  }, []);
  const deleteTournament = useDeleteTournament();

  // "Last opened": where Tabroom assignments go, and what "New round" etc. default to.
  useEffect(() => {
    void (async () => {
      const s = await deps.settings.get();
      if (s.currentTournamentId !== tournament.id) await deps.settings.put({ ...s, currentTournamentId: tournament.id });
    })();
  }, [tournament.id, deps]);

  if (!rounds || latestAnywhere === undefined) return null;
  const next = rounds.find((r) => r.status !== 'submitted' && r.status !== 'committed');
  const formatId = nextRoundFormatId(rounds, latestAnywhere, tournament.formatId);
  const events = eventsJudged(rounds);
  const mixed = events.length > 1;
  const modal = url.get('modal');
  const modalRound = rounds.find((r) => r.id === url.get('round'));
  const closeModal = () => url.close('modal', 'round');
  const openModal = (m: string, round?: Round) => url.set({ modal: m, round: round?.id ?? null });
  const when = dateRange(tournament.startDate, tournament.endDate, Date.now());

  return (
    <PageShell
      crumbs={[{ label: 'Tournaments', href: paths.dashboard }]}
      title={
        <Group gap={6} wrap="nowrap" component="span">
          <span>{tournament.name}</span>
          <Menu position="bottom-start">
            <Menu.Target><ActionIcon size="sm" aria-label="Tournament menu"><IconDots size={14} /></ActionIcon></Menu.Target>
            <Menu.Dropdown>
              <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => openModal('edit-tournament')}>Edit name and dates</Menu.Item>
              <Menu.Divider />
              <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={async () => { if (await deleteTournament(tournament)) url.navigate(paths.dashboard, { replace: true }); }}>Delete tournament</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      }
      subtitle={[when, ...events].filter(Boolean).join(' · ')}
    >
      {next && (
        <Section title="Next up">
          <Card padding="md" className="hero">
            <Group justify="space-between" align="flex-start" wrap="wrap">
              <Box>
                <Text size="sm" c="dimmed">
                  {next.label}{next.flight && ` · Flight ${next.flight}`}{next.startTime && ` · ${next.startTime}`}{next.room && ` · Room ${next.room}`}
                </Text>
                <Text fw={600} size="lg">{next.teamA.code} <Text span c="dimmed" fw={400}>vs</Text> {next.teamB.code}</Text>
                <Text size="sm" c="dimmed">{sidesSummary(next)} · {next.format.name}</Text>
              </Box>
              <Group>
                <Button leftSection={<IconCoin size={16} />} onClick={() => openModal('sides', next)}>{next.format.order === 'coinFlip' ? 'Coin flip' : 'Sides'}</Button>
                <Button component={Link} href={paths.round(tournament.id, next.id)} variant="filled" data-testid="start-round">Start round →</Button>
              </Group>
            </Group>
          </Card>
        </Section>
      )}

      <Section title="Rounds" right={<Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => openModal('new-round')} data-testid="add-round">Add round</Button>}>
        {rounds.length === 0 && (
          <Card withBorder padding="lg">
            <Text c="dimmed" size="sm" mb="sm">No rounds yet. Add one when you get your pairing (the round number, room, and the two team codes).</Text>
            <Button variant="filled" leftSection={<IconPlus size={14} />} onClick={() => openModal('new-round')}>Add first round</Button>
          </Card>
        )}
        <Stack gap={0}>
          {rounds.map((r) => (
            <Group key={r.id} justify="space-between" py={8} px={4} wrap="nowrap" style={{ borderBottom: '1px solid var(--grid-line)' }} data-testid="round-row">
              <Box style={{ minWidth: 0, flex: 1 }}>
                <Box component={Link} href={roundHref(r)} style={{ display: 'flex', gap: 'var(--mantine-spacing-md)', color: 'inherit', textDecoration: 'none', minWidth: 0 }}>
                  <Text fw={600} w={40} style={{ flexShrink: 0 }}>{r.label}</Text>
                  <Text size="sm" c="dimmed" w={48} className="tabular" style={{ flexShrink: 0 }}>{r.startTime || '—'}</Text>
                  <Box style={{ minWidth: 0 }}>
                    <Text size="sm" lineClamp={1}>{r.teamA.code} v {r.teamB.code}</Text>
                    <Text size="xs" c="dimmed" lineClamp={1}>{r.room ? `Room ${r.room} · ` : ''}{sidesSummary(r)}{mixed ? ` · ${r.format.name}` : ''}</Text>
                  </Box>
                </Box>
                <Box pl={104}><RoundSummary round={r} /></Box>
              </Box>
              <Group gap={4} wrap="nowrap">
                <Badge variant="light" color={statusLabel[r.status].color}>{statusLabel[r.status].label}</Badge>
                <Menu position="bottom-end">
                  <Menu.Target><ActionIcon aria-label={`Round ${r.label} menu`}><IconDots size={16} /></ActionIcon></Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item leftSection={<IconCoin size={14} />} onClick={() => openModal('sides', r)}>{r.format.order === 'coinFlip' ? 'Coin flip / sides' : 'Sides'}</Menu.Item>
                    <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => openModal('edit-round', r)}>Edit details</Menu.Item>
                    <Menu.Divider />
                    <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => { if (confirm(`Delete ${r.label} and its flow? This can't be undone.`)) void uc.deleteRound(r.id); }}>Delete</Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            </Group>
          ))}
        </Stack>
      </Section>

      <Text size="xs" c="dimmed">Shortcuts: <K>⌘G</K> glossary · <K>⌘/</K> cheatsheet</Text>

      <Modal opened={modal === 'new-round'} onClose={closeModal} title="Add round" size="lg">
        <RoundForm
          initial={emptyRoundForm(`R${rounds.length + 1}`, formatId, suggestedResolution(rounds, formatId))}
          suggestResolution={(fid) => suggestedResolution(rounds, fid)}
          submitLabel="Add round"
          onCancel={closeModal}
          onSubmit={async (v, fid) => {
            const r = await uc.createRound(tournament.id, v, formatById(fid) ?? DEFAULT_FORMAT);
            if (r.ok) closeModal();
            return r.ok ? null : r.error;
          }}
        />
      </Modal>
      <Modal opened={modal === 'edit-round' && !!modalRound} onClose={closeModal} title="Edit round" size="lg">
        {modal === 'edit-round' && modalRound && <EditRound round={modalRound} onDone={closeModal} />}
      </Modal>
      {modal === 'sides' && modalRound && <CoinFlipModal round={modalRound} opened onClose={closeModal} />}
      <Modal opened={modal === 'edit-tournament'} onClose={closeModal} title="Edit tournament">
        <TournamentForm
          initial={{ name: tournament.name, startDate: tournament.startDate ?? '', endDate: tournament.endDate ?? '' }}
          submitLabel="Save"
          onCancel={closeModal}
          onSubmit={async (v) => {
            const r = await uc.updateTournament(tournament.id, v);
            if (r.ok) closeModal();
            return r.ok ? null : r.error;
          }}
        />
      </Modal>
    </PageShell>
  );
};

/** Edit form, once the round's names (on-device only) have loaded. */
const EditRound = ({ round: r, onDone }: { round: Round; onDone: () => void }) => {
  const deps = useDeps();
  const uc = useUseCases();
  const names = useLiveQuery(async () => (await deps.names.get(r.id)) ?? null, [r.id]);
  if (names === undefined) return null;
  return (
    <RoundForm
      initial={{
        label: r.label, room: r.room, flight: r.flight, startTime: r.startTime,
        teamA: { ...r.teamA }, teamB: { ...r.teamB },
        formatId: r.format.id,
        resolution: r.resolution ?? '',
        teamANames: [names?.teamA[0] ?? '', names?.teamA[1] ?? ''],
        teamBNames: [names?.teamB[0] ?? '', names?.teamB[1] ?? ''],
      }}
      submitLabel="Save"
      onCancel={onDone}
      onSubmit={async (v, fid) => {
        const f = await uc.changeRoundFormat(r.id, fid);
        if (!f.ok) return f.error;
        const res = await uc.updateRoundDetails(r.id, v);
        if (res.ok) onDone();
        return res.ok ? null : res.error;
      }}
    />
  );
};
